import React, { useCallback, useEffect, useState } from 'react';
import { css } from '../../../../../services/css';
import { getTextFromFile, promptFile } from '../../../../../services/file';
import { Notify } from '../../../../../services/notification/notify';
import { parseRawToContacts } from '../../../../../services/parsing/parseRaw';
import { validateContact } from '../../../../../services/validation/contact';
import { NoteCard } from '../../../../shared/cards/noteCard';
import { Grid } from '../../../../shared/grid/grid';
import { PageController } from '../../../../shared/grid/queryGrid/pageController';
import { Button } from '../../../../shared/inputs/button';
import { Portal } from '../../../../shared/portal/portal';
import { ReactComponent as DontDropIcon } from './../../../../../assets/icons/dont-drop-file.svg';
import { ReactComponent as DropIcon } from './../../../../../assets/icons/drop-file.svg';
import { ReactComponent as ErrorIcon } from './../../../../../assets/icons/error.svg';

import './campaignAdhocContactImport.css';

const DEMO_TABLE_COLUMNS = {
    phoneNumber: {
        label: 'Phone Number',
        sortable: false,
    },
    patientId: {
        label: 'Patient Id',
        sortable: false,
    },
    firstName: {
        label: 'First Name',
        sortable: false,
    },
    lastName: {
        label: 'Last Name',
        sortable: false,
    },
    branchName: {
        label: 'Branch Name',
        sortable: false,
    },
};

const PAGE_SIZE = 500;

export const CampaignAdhocContactImport = ({
    isShown = true,
    setContacts = (_) => {},
}) => {
    const [rawText, setRawText] = useState(null);
    const [data, setData] = useState(null);
    const [tableData, setTableData] = useState([]);
    const [dataHasError, setDataHasError] = useState(false);
    const [showDropMenu, setShowDropMenu] = useState(false);
    const [validFile, setValidFile] = useState(false);
    const [page, setPage] = useState(0);
    const [pageData, setPageData] = useState({
        totalPages: 0,
        totalCount: 0,
    });

    const [tableColumns, setTableColumns] = useState({
        phoneNumber: {
            label: 'Phone Number',
            sortable: false,
        },
        patientId: {
            label: 'Patient Id',
            sortable: false,
        },
        firstName: {
            label: 'firstName',
            sortable: false,
        },
        lastName: {
            label: 'lastName',
            sortable: false,
        },
        branchName: {
            label: '',
            sortable: false,
            size: '100px',
        },
    });

    const onPaste = useCallback(
        (e) => {
            if (!isShown) return;
            if (rawText) return;

            const clipboardData = e.clipboardData || window.clipboardData;
            const pastedData = clipboardData.getData('Text');

            setRawText(pastedData.replace(/\t/g, ','));
        },
        [isShown, rawText]
    );

    const onDrag = useCallback(
        (e) => {
            if (!isShown) return;

            e.preventDefault();
            if (e.type === 'dragenter' && e.relatedTarget === null) {
                setShowDropMenu(true);

                setValidFile(e.dataTransfer.items?.[0]?.type === 'text/csv');
            }
            if (e.type === 'dragleave' && e.relatedTarget === null) {
                setShowDropMenu(false);
            }
        },
        [isShown]
    );

    const onDrop = useCallback(
        (e) => {
            if (!isShown) return;

            e.preventDefault();
            setShowDropMenu(false);

            if (e.dataTransfer.items?.[0]?.type === 'text/csv')
                processFile(e.dataTransfer.files[0]);
        },
        [isShown]
    );

    const processFile = (file) => {
        getTextFromFile(file, (text) => setRawText(text));
    };

    useEffect(() => {
        if (!data) return;

        let foundError = false;
        let newTableData = (data ?? []).map((r, i) => {
            const errors = validateContact(r);
            if (errors.PatientId || errors.PhoneNumber) foundError = true;

            let obj = {
                id: r.key,
                delete: (
                    <Button
                        className='primary thin'
                        onClick={() => {
                            data.splice(i, 1);
                            setData([...data]);
                        }}>
                        Remove
                    </Button>
                ),
                PhoneNumber: (
                    <div
                        className={css(
                            'cell',
                            errors.PhoneNumber && 'invalid'
                        )}>
                        {errors.PhoneNumber ? (
                            <ErrorIcon title={errors.PhoneNumber} />
                        ) : (
                            ''
                        )}

                        <input
                            className='text'
                            value={r.PhoneNumber ?? ''}
                            onChange={(e) => {
                                data[i].PhoneNumber = e.target.value;
                                setData([...data]);
                            }}
                        />
                    </div>
                ),
                PatientId: (
                    <div className={css('cell', errors.PatientId && 'invalid')}>
                        {errors.PatientId ? (
                            <ErrorIcon title={errors.PatientId} />
                        ) : (
                            ''
                        )}

                        <input
                            className='text'
                            value={r.PatientId ?? ''}
                            onChange={(e) => {
                                data[i].PatientId = e.target.value;
                                setData([...data]);
                            }}
                        />
                    </div>
                ),
            };
            const columns = Object.keys(tableColumns);

            for (let j = 0; j < columns.length; j++) {
                if (['delete', 'PhoneNumber', 'PatientId'].includes(columns[j]))
                    continue;

                obj[columns[j]] = (
                    <div className='cell'>
                        <input
                            className='text'
                            value={r[columns[j]] ?? ''}
                            onChange={(e) => {
                                data[i][columns[j]] = e.target.value;
                                setData([...data]);
                            }}
                        />
                    </div>
                );
            }

            return obj;
        });

        setDataHasError(foundError);
        setTableData(newTableData);
    }, [data, tableColumns]);

    const getPageData = useCallback(() => {
        return tableData.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
    }, [tableData, page]);

    useEffect(() => {
        const length = tableData.length;

        setPageData({
            totalPages: Math.ceil(length / PAGE_SIZE),
            totalCount: length,
        });
    }, [tableData]);

    useEffect(() => {
        window.addEventListener('paste', onPaste);
        document.addEventListener('dragenter', onDrag);
        document.addEventListener('dragleave', onDrag);
        document.addEventListener('dragover', onDrag);
        document.addEventListener('drop', onDrop);

        return () => {
            window.removeEventListener('paste', onPaste);
            document.addEventListener('dragenter', onDrag);
            document.addEventListener('dragleave', onDrag);
            document.addEventListener('dragover', onDrag);
            document.addEventListener('drop', onDrop);
        };
    }, [onPaste, onDrag, onDrop]);

    useEffect(() => {
        if (!rawText) return;

        let parsedRaw = null;

        try {
            parsedRaw = parseRawToContacts(rawText, {});
        } catch (ex) {
            Notify.error(
                'Error importing contacts',
                'There was an issue importing contacts. Please ensure the correct format is used.'
            );
            setRawText(null);
        }

        const headers = Object.keys(parsedRaw?.[0] ?? {});

        if (headers.length > 0) {
            let obj = {
                PhoneNumber: {
                    label: 'PhoneNumber',
                    sortable: false,
                },
                PatientId: {
                    label: 'PatientId',
                    sortable: false,
                },
            };

            for (let i = 0; i < headers.length; i++) {
                if (headers[i] === 'key') continue;

                obj[headers[i]] = {
                    label: headers[i],
                    sortable: false,
                };
            }

            obj.delete = {
                label: '',
                sortable: false,
                size: '125px',
            };

            setTableColumns(obj);
        }

        setData(parsedRaw);
    }, [rawText]);

    const submitData = () => {
        setContacts(
            (data ?? []).map((r) => {
                let row = { ...r };
                delete row.key;
                return row;
            })
        );
    };

    const clearEmptyPhoneNumbers = () => {
        if (!data) return;

        setData(data.filter(d => !!d.PhoneNumber));
    };

    return (
        <div className='contact-import-wrapper'>
            <Portal>
                <div
                    className={
                        'contact-import-drop' +
                        (showDropMenu ? ' active' : '') +
                        (!validFile ? ' invalid' : '')
                    }>
                    <main>
                        {validFile ? <DropIcon /> : <DontDropIcon />}
                        <h1>
                            {validFile ? 'Drop CSV File' : "File isn't a CSV"}
                        </h1>
                    </main>
                </div>
            </Portal>

            <NoteCard
                editButton={false}
                deleteButton={false}
                thin={true}
                title='Valid Data'>
                It's important the data here is correct even if its not part of
                the immediate customer experience. For example, an invalid
                Patient Id or Sales Order Number could confuse the logging
                system. If the validity of the data isn't certain please exclude
                it.
            </NoteCard>

            <div className='contact-import-view'>
                {!rawText ? (
                    <div className='split'>
                        <div>
                            <h1>Import Options</h1>
                            <h2>From your clipboard</h2>
                            <p>
                                Highlight the data in Excel, including the
                                column headers, and then copy with{' '}
                                <code>Ctrl + C</code>.
                            </p>
                            <p>
                                Click anywhere in this page and then paste your
                                clipboard with <code>Ctrl + V</code>.
                            </p>
                            <h2>Drop a .csv file</h2>
                            <p>Drop a .csv file anywhere in this page.</p>
                            <h2>Open a .csv file</h2>
                            <p>Select the .csv file below.</p>
                            <Button
                                className='shadow'
                                onClick={() => promptFile(processFile)}>
                                Open
                            </Button>
                        </div>
                        <div>
                            <h1>Import Format</h1>
                            <h2>Required Columns</h2>
                            <ol className='columns'>
                                <li>
                                    Phone Number
                                    <ul>
                                        <li>The phone number of the patient</li>
                                    </ul>
                                </li>
                                <li>
                                    Patient Id
                                    <ul>
                                        <li>
                                            The id of the patient for logging
                                            purposes
                                        </li>
                                    </ul>
                                </li>
                            </ol>

                            <p>
                                Any other columns can be included which will be
                                logged. For example: <code>FirstName</code>,{' '}
                                <code>LastName</code>,<code>BranchName</code>,
                                etc.
                            </p>

                            <h2>Example</h2>
                            <Grid
                                columns={DEMO_TABLE_COLUMNS}
                                data={[
                                    {
                                        id: 'demo1',
                                        phoneNumber: '+12843849354',
                                        patientId: '28382',
                                        firstName: 'John',
                                        lastName: 'Doe',
                                        branchName: 'Branch A',
                                    },
                                    {
                                        id: 'demo2',
                                        phoneNumber: '(392) 473-9953',
                                        patientId: '7954',
                                        firstName: 'Jane',
                                        lastName: 'Smith',
                                        branchName: 'Branch B',
                                    },
                                ]}
                            />
                        </div>
                    </div>
                ) : (
                    <>
                        <header className='contact-import-header'>
                            <Button
                                className='shadow'
                                type='button'
                                title='Clear the current import and return'
                                onClick={() => setRawText(null)}>
                                Reset
                            </Button>

                            <Button
                                className='shadow primary'
                                title='Add a new row'
                                onClick={() => {
                                    setData([{}, ...data]);
                                }}>
                                Add Row
                            </Button>

                            <Button
                                className='shadow'
                                type='button'
                                title='Removes all records with an empty phone number'
                                onClick={() => clearEmptyPhoneNumbers()}>
                                Clear Blank Numbers
                            </Button>

                            <h4>Importing {tableData.length} contacts.</h4>
                        </header>
                        <Grid
                            className='grid'
                            data={getPageData()}
                            columns={tableColumns}
                        />
                        <PageController
                            pageIndex={page}
                            totalPages={pageData.totalPages}
                            pageState={[page, setPage]}
                            loading={false}
                        />
                        <br /> <br />
                        {!data
                            ? "Data isn't loaded yet"
                            : dataHasError
                            ? 'There are errors in the data above. Fix those before importing.'
                            : data?.length === 0
                            ? 'There are no zip codes to import.'
                            : 'The data above is valid.'}
                        <br />
                        <br />
                        <button
                            className='shadow primary use-contacts-btn'
                            type='button'
                            disabled={
                                !data || dataHasError || data?.length === 0
                            }
                            onClick={submitData}>
                            Use these contacts
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};
